import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, FileText, Loader2, Pencil, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  deleteDocIntelligenceTemplate,
  listDocIntelligenceTemplates
} from '../../services/api/implementation/impl';
import { formatCreatedUpdatedLine } from './docIntelUtils';
import { useNowTick } from './useNowTick';

const STATUS_BADGE = {
  draft: 'rounded-md bg-amber-50 text-amber-800 ring-1 ring-amber-200/80',
  active: 'rounded-md bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200/80'
};

const STATUS_LABEL = {
  draft: 'Draft',
  active: 'Live'
};

const COLLAPSE_AFTER = 3;

const DocIntelTemplateDrafts = ({ projectId, selectedId, onSelect, onDeleted }) => {
  const queryClient = useQueryClient();
  const now = useNowTick(60_000);
  const [listOpen, setListOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // all | draft | active

  useEffect(() => {
    if (selectedId) setListOpen(true);
  }, [selectedId]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['docIntelTemplates', projectId],
    queryFn: () => listDocIntelligenceTemplates(projectId, 1, 100),
    enabled: Boolean(projectId),
    refetchOnMount: 'always',
    staleTime: 15_000
  });

  const deleteMutation = useMutation({
    mutationFn: (templateId) => deleteDocIntelligenceTemplate(templateId),
    onSuccess: (result, templateId) => {
      toast.success(
        result?.deleted_linked_template_id
          ? 'Template deleted (property document removed too)'
          : 'Template deleted'
      );
      queryClient.invalidateQueries({ queryKey: ['docIntelTemplates', projectId] });
      onDeleted?.(templateId);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to delete template');
    }
  });

  const templates = data?.templates ?? [];

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return templates;
    return templates.filter((tpl) => (tpl.status || 'draft').toLowerCase() === statusFilter);
  }, [templates, statusFilter]);

  const visible = expanded ? filtered : filtered.slice(0, COLLAPSE_AFTER);
  const hiddenCount = Math.max(0, filtered.length - COLLAPSE_AFTER);
  const draftCount = templates.filter((t) => (t.status || 'draft').toLowerCase() === 'draft').length;
  const activeCount = templates.filter((t) => (t.status || '').toLowerCase() === 'active').length;

  if (isLoading) {
    return (
      <section className="mb-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin text-teal-700" />
        Loading saved templates…
      </section>
    );
  }

  if (isError || !templates.length) {
    return null;
  }

  return (
    <section className="mb-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm ring-1 ring-slate-900/[0.03]">
      <button
        type="button"
        onClick={() => setListOpen((v) => !v)}
        className="flex w-full flex-wrap items-center justify-between gap-2 bg-gradient-to-r from-slate-50/80 to-white px-4 py-2.5 text-left hover:bg-slate-50">
        <div>
          <h2 className="text-base font-semibold text-slate-900">
            Saved templates
            <span className="ml-2 text-sm font-medium text-slate-600">
              {templates.length} · {draftCount} draft · {activeCount} live
            </span>
          </h2>
          <p className="text-sm text-slate-600">
            {listOpen
              ? 'Template status: Draft or Live (promoted). Separate from file upload status below.'
              : 'Collapsed — open to resume a draft or open a live template.'}
          </p>
        </div>
        {listOpen ? (
          <ChevronUp className="h-4 w-4 text-slate-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-400" />
        )}
      </button>

      {listOpen && (
        <>
          <div className="flex flex-wrap items-center gap-1 border-t border-slate-100 px-4 py-2">
            {[
              ['all', 'All'],
              ['draft', 'Draft'],
              ['active', 'Live']
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setStatusFilter(key);
                  setExpanded(false);
                }}
                className={`rounded-md px-2.5 py-1 text-xs font-bold transition ${
                  statusFilter === key
                    ? 'bg-[#2A565E] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}>
                {label}
              </button>
            ))}
          </div>
          <ul className="divide-y divide-slate-100 border-t border-slate-100">
            {visible.map((tpl) => {
              const isSelected = selectedId === tpl.id;
              const statusKey = (tpl.status || 'draft').toLowerCase();
              const badgeClass = STATUS_BADGE[statusKey] || STATUS_BADGE.draft;
              const timeLine = formatCreatedUpdatedLine(tpl.created_at, tpl.updated_at, now);
              const isDeleting =
                deleteMutation.isPending && deleteMutation.variables === tpl.id;

              return (
                <li key={tpl.id}>
                  <div
                    className={`flex w-full items-center gap-2 px-3 py-2.5 transition sm:px-4 ${
                      isSelected ? 'bg-teal-50/50' : 'hover:bg-slate-50/80'
                    }`}>
                    <button
                      type="button"
                      onClick={() => onSelect(tpl.id)}
                      className="flex min-w-0 flex-1 items-center gap-2.5 text-left">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-50 text-teal-800 ring-1 ring-teal-100">
                        <FileText className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-semibold text-slate-900">
                        {tpl.name || `Template #${tpl.id}`}
                      </p>
                          <span
                            className={`shrink-0 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${badgeClass}`}>
                            {STATUS_LABEL[statusKey] || statusKey}
                          </span>
                        </div>
                        <p
                          className="mt-0.5 truncate text-xs font-medium leading-snug text-slate-600"
                          title={timeLine}>
                          {timeLine || `ID ${tpl.id}`}
                          {tpl.linked_template_id
                            ? ` · Property doc #${tpl.linked_template_id}`
                            : ''}
                        </p>
                      </div>
                      <Pencil className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                    </button>
                    <button
                      type="button"
                      title="Delete template"
                      disabled={isDeleting}
                      onClick={() => {
                        const name = tpl.name || `Template #${tpl.id}`;
                        const linkedNote = tpl.linked_template_id
                          ? ` This will also remove property document #${tpl.linked_template_id} if nothing else uses it.`
                          : '';
                        if (
                          !window.confirm(
                            `Delete template “${name}”?${linkedNote}\n\nThis cannot be undone.`
                          )
                        ) {
                          return;
                        }
                        deleteMutation.mutate(tpl.id);
                      }}
                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-red-50 hover:text-red-600 disabled:opacity-50">
                      {isDeleting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="flex w-full items-center justify-center gap-1.5 border-t border-slate-100 bg-slate-50/60 px-3 py-2 text-xs font-semibold text-teal-800 hover:bg-slate-100">
              {expanded ? (
                <>
                  <ChevronUp className="h-3.5 w-3.5" />
                  Show fewer
                </>
              ) : (
                <>
                  <ChevronDown className="h-3.5 w-3.5" />
                  Show {hiddenCount} more template{hiddenCount === 1 ? '' : 's'}
                </>
              )}
            </button>
          )}
          {!visible.length && (
            <p className="px-4 py-6 text-center text-sm text-slate-500">
              No {statusFilter === 'all' ? '' : statusFilter} templates in this filter.
            </p>
          )}
        </>
      )}
    </section>
  );
};

export default DocIntelTemplateDrafts;
